import { useCallback, useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { theme, card, btnPrimary, inputStyle, type as text } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate } from "../lib/format";
import { runAiJob } from "../lib/aiJob";
import { secondaryBtn } from "../pages/resilience/shared";

// Minutes read into a management review (docs/MANAGEMENT-SYSTEM.md "Minutes read into the review", backend
// lib/minutesReading.ts). The minutes of the meeting, already in the Evidence Repository, are read by the AI into a
// proposed conclusion, decisions and agreed actions, each with the words of the minutes it rests on. Nothing reaches the
// review until a member confirms it; a confirmed action is also raised as a corrective action. controls:write on the server.

interface Proposal {
  id: string; kind: "conclusion" | "decision" | "action"; label: string; text: string; decisionKind: string;
  ownerName: string | null; dueDate: string | null; quote: string; status: string; correctiveActionId: string | null;
}
interface View {
  reading: { id: string; fileName: string; documentVersion: number; status: string; error: string | null; truncated: boolean; dropped: number; createdAt: string } | null;
  proposals: Proposal[];
}
interface Candidate { id: string; fileName: string; version: number; uploadedAt: string }

const TONE: Record<string, StatusTone> = { pending: "partial", confirmed: "met", edited: "met", rejected: "none", superseded: "none" };
const LABEL: Record<string, string> = { pending: "To confirm", confirmed: "Recorded", edited: "Recorded (edited)", rejected: "Not recorded", superseded: "Replaced" };

export function ReviewMinutesReader({ reviewId, onChanged }: { reviewId: string; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/management-system/reviews/${reviewId}/minutes`;

  const load = useCallback(async () => {
    try { setView(await api.get<View>(base)); } catch (e) { setError(apiErrorMessage(e, "Could not load the minutes.")); }
  }, [base]);
  useEffect(() => {
    if (!open) return;
    void load();
    api.get<{ documents: Candidate[] }>("/tprm/signed-copy-candidates").then((r) => setCandidates(r.documents)).catch(() => setCandidates([]));
  }, [open, load]);

  async function read() {
    if (!picked) return;
    setBusy(true); setError(null);
    try { await runAiJob(base, (id) => `/management-system/minutes-jobs/${id}`, { documentId: picked }); }
    catch (e) { setError(apiErrorMessage(e, "Could not read the minutes.")); }
    finally { setBusy(false); setPicked(""); await load(); }
  }
  async function decide(p: Proposal, body: Record<string, unknown>) {
    setError(null);
    try { setView((await api.post<{ view: View }>(`${base}/proposals/${p.id}`, body)).view); onChanged(); }
    catch (e) { setError(apiErrorMessage(e, "Could not record that.")); }
  }

  if (!open) return <button type="button" style={{ ...secondaryBtn, marginTop: 6, display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => setOpen(true)}><FileText size={12} /> Read the minutes</button>;
  return (
    <div style={{ ...card, marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ ...text.label }}>Minutes read into this review</div>
        <button type="button" style={secondaryBtn} onClick={() => setOpen(false)}>Close</button>
      </div>
      <div style={{ ...text.hint, marginBottom: 8 }}>Pick the minutes from the Evidence Repository. The AI proposes the conclusion, decisions and actions with the words they rest on; nothing is added to the review until you confirm it.</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <select aria-label="Minutes to read" value={picked} onChange={(e) => setPicked(e.target.value)} style={{ ...inputStyle, minWidth: 260, flex: 1 }}>
          <option value="">Choose the minutes…</option>
          {candidates.map((c) => <option key={c.id} value={c.id}>{c.fileName} (v{c.version}, {fmtDate(c.uploadedAt)})</option>)}
        </select>
        <button type="button" disabled={!picked || busy} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }} onClick={() => void read()}>{busy ? "Reading…" : "Read them"}</button>
      </div>
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 6 }}>{error}</div>}
      {view?.reading && (
        <div style={{ ...text.meta, marginBottom: 6 }}>
          {view.reading.fileName} v{view.reading.documentVersion}, read {fmtDate(view.reading.createdAt)}
          {view.reading.status === "error" && view.reading.error ? ` — ${view.reading.error}` : ""}
          {view.reading.dropped ? ` · ${view.reading.dropped} left out because the words quoted were not in the minutes` : ""}
        </div>
      )}
      {view?.proposals.map((p) => <ProposalRow key={p.id} p={p} onDecide={(b) => void decide(p, b)} />)}
    </div>
  );
}

function ProposalRow({ p, onDecide }: { p: Proposal; onDecide: (body: Record<string, unknown>) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(p.text);
  const [due, setDue] = useState(p.dueDate ?? "");
  return (
    <div style={{ padding: "8px 0", borderTop: `1px solid ${theme.color.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={text.label}>{p.label}{p.ownerName ? ` · ${p.ownerName}` : ""}{p.dueDate ? ` · by ${p.dueDate}` : ""}</div>
          <div style={text.body}>{p.text}</div>
          <blockquote style={{ ...text.hint, margin: "4px 0 0", paddingLeft: 8, borderLeft: `2px solid ${theme.color.border}`, fontStyle: "italic" }}>{p.quote}</blockquote>
          {p.correctiveActionId && <a href={`/improvement?action=${p.correctiveActionId}`} style={{ ...text.hint, color: theme.color.ref }}>Corrective action raised</a>}
        </div>
        <StatusTag tone={TONE[p.status] ?? "none"}>{LABEL[p.status] ?? p.status}</StatusTag>
      </div>
      {p.status === "pending" && !editing && (
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <button type="button" style={{ ...btnPrimary, padding: "3px 10px", fontSize: 12 }} onClick={() => onDecide({ decision: "confirm" })}>{p.kind === "action" ? "Record and raise action" : "Record it"}</button>
          <button type="button" style={secondaryBtn} onClick={() => setEditing(true)}>Edit</button>
          <button type="button" style={secondaryBtn} onClick={() => onDecide({ decision: "reject" })}>Not right</button>
        </div>
      )}
      {editing && (
        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <input aria-label="What was recorded" value={draft} onChange={(e) => setDraft(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 240 }} maxLength={2000} />
          {p.kind === "action" && <input type="date" aria-label="Due date" value={due} onChange={(e) => setDue(e.target.value)} style={{ ...inputStyle, width: 160 }} />}
          <button type="button" style={{ ...btnPrimary, padding: "3px 10px", fontSize: 12 }} onClick={() => { onDecide({ decision: "edit", text: draft, ...(p.kind === "action" ? { dueDate: due || null } : {}) }); setEditing(false); }}>Save</button>
          <button type="button" style={secondaryBtn} onClick={() => setEditing(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}
